import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { cardSrc, parseCards } from './cards/cardUtils';
import useCardStripToggle from '../../hooks/casino/useCardStripToggle';

const AB_CARD_BASE = 'https://wver.sprintstaticdata.com/v230/static/front/img/andar-bahar-cards/';

/** Position index (1-13) for each card suffix */
const SUFFIX_POS = {
  A: 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7,
  '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13,
};

function getCardPos(nat) {
  const suffix = nat.split(' ').slice(1).join(' ');
  return SUFFIX_POS[suffix] ?? 0;
}

// API card string → andar/bahar dealt cards.
// 1) split by parity (bahar = even idx, andar = odd idx)
// 2) placeholder '1'/'0' (undealt/closed) hata do — reference slider undealt
//    cards nahi dikhata (all-1s => empty slider)
// 3) reverse — API oldest-first bhejta hai; owl-rtl ke saath reverse karne se
//    dealing order sahi dikhta hai (oldest left, newest right) = reference match
function splitAbCards(card) {
  const all = parseCards(card);
  const isReal = (c) => c && c !== '1' && c !== '0';
  const andarCards = all.filter((_, i) => i % 2 === 1).filter(isReal).reverse();
  const baharCards = all.filter((_, i) => i % 2 === 0).filter(isReal).reverse();
  return { andarCards, baharCards };
}

function isTruthy(v) {
  return v && v !== '0' && v !== 0;
}

// owl-item slot = 38px card + 2px margin-left. Measured on tenexch.com/admin
// ab3: 7 items -> owl-stage width 280px, 570px outer -> 14 cards across. The
// old 37px stage was narrower than its own items, so cards wrapped into a
// second line under the last slots.
const VIDEO_ITEM_W = 40;

// Owl Carousel 2 replica (dealt cards) — reference DOM exact: owl-rtl (RTL flow,
// isliye pehle sequence ulti dikh rahi thi), id=andarSlider/baharSlider, owl-item
// style width:auto margin-left:2px. Package CSS owl.carousel.css me.
export const VideoOwl = memo(function VideoOwl({ cards, sliderId, extraClass, variant }) {
  // dum (Dus ka Dum): perView 3 fixed, item width = container/3 (no margin) —
  // reference owl-item width ~48px. Default (AB): perView measured, width auto +
  // margin-left 2px.
  const dum = variant === 'dum';
  const outerRef = useRef(null);
  const [page, setPage] = useState(0);
  const [perView, setPerView] = useState(dum ? 3 : 6);
  const [dumItemW, setDumItemW] = useState(48);

  useLayoutEffect(() => {
    const measure = () => {
      const w = outerRef.current?.clientWidth || 0;
      if (w <= 0) return;
      if (dum) { setPerView(3); setDumItemW(w / 3); }
      else setPerView(Math.max(1, Math.floor(w / VIDEO_ITEM_W)));
    };
    measure();
    window.addEventListener('resize', measure);
    // phone pe overlay pehle 0 width pe mount hota hai (dum item 48px atak
    // jata tha; reference 28.75px = outer/3) — element resize pe re-measure
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && outerRef.current) ro.observe(outerRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, [dum]);

  const slot = dum ? dumItemW : VIDEO_ITEM_W;
  const list = cards.length ? cards : [];
  const maxPage = Math.max(0, Math.ceil(list.length / perView) - 1);
  const cur = Math.min(page, maxPage);
  const atStart = cur <= 0;
  const atEnd = cur >= maxPage;
  const translateX = -(cur * perView * slot);
  const stageWidth = list.length * slot;
  const itemStyle = dum ? { width: `${dumItemW}px` } : { width: 'auto', marginLeft: '2px' };

  const prev = useCallback(() => setPage((p) => Math.max(0, p - 1)), []);
  const next = useCallback(() => setPage((p) => p + 1), []);

  return (
    <div id={sliderId} className={`${extraClass ? `${extraClass} ` : ''}ab-slider owl-carousel owl-theme owl-rtl owl-loaded owl-drag`}>
      <div className="owl-stage-outer" ref={outerRef}>
        <div
          className="owl-stage"
          style={{ transform: `translate3d(${translateX}px, 0px, 0px)`, transition: 'all', width: `${stageWidth}px` }}
        >
          {list.map((code, i) => {
            const active = i >= cur * perView && i < (cur + 1) * perView;
            return (
              <div key={i} className={`owl-item${active ? ' active' : ''}`} style={itemStyle}>
                <div className="item">
                  <span><img src={cardSrc(code)} /></span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Reference: the nav is hidden only while the row has NO card; with
          cards it stays, its buttons disabled at the ends. */}
      <div className={`owl-nav${list.length === 0 ? ' disabled' : ''}`}>
        <button type="button" role="presentation" className={`owl-prev${atStart ? ' disabled' : ''}`} onClick={atStart ? undefined : prev}>
          <span aria-label="Previous">‹</span>
        </button>
        <button type="button" role="presentation" className={`owl-next${atEnd ? ' disabled' : ''}`} onClick={atEnd ? undefined : next}>
          <span aria-label="Next">›</span>
        </button>
      </div>
      <div className="owl-dots disabled"></div>
    </div>
  );
});

// casino-video ke andar dealt-cards overlay (reference: d-none-small, shuffle
// grip + col-1 A/B labels + col-11 do owl carousels — andar/bahar). AB4 page
// se render hota hai (casino-video ke andar, casino-detail me nahi).
// roundId: the "||" grip closes / reopens the strip for the round
// (useCardStripToggle); it stays closed until a card is dealt (user 2026-10-14).
export const AbVideoCards = memo(function AbVideoCards({ card = '', roundId = '' }) {
  const { andarCards, baharCards } = splitAbCards(card);
  const [stripCollapsed, toggleStrip] = useCardStripToggle(roundId);
  const hasCards = andarCards.length + baharCards.length > 0;
  return (
    // d-none-mobile (hidden below 1280px): the reference has no video overlay
    // there - the casino-detail strip (d-none-desktop) shows the cards instead;
    // d-none-small left both on screen between 768 and 1279px.
    <div className={`casino-video-cards d-none-mobile${hasCards && !stripCollapsed ? '' : ' hide-cards'}`}>
      <div className="casino-cards-shuffle" role="button" style={{ cursor: 'pointer' }} onClick={toggleStrip}>
        <i className="fas fa-grip-lines-vertical"></i>
      </div>
      {' '}
      <div className="casino-video-cards-container">
        <div className="row row5 align-items-center">
          <div className="col-1">
            <div className="row row5"><div className="col-12 mb-3"><b>A</b></div></div>
            {' '}
            <div className="row row5"><div className="col-12"><b>B</b></div></div>
          </div>
          {' '}
          <div className="col-11">
            <div className="card-inner mb-1">
              <div className="row row5"><div className="col-12"><VideoOwl cards={andarCards} sliderId="andarSlider" /></div></div>
            </div>
            {' '}
            <div className="card-inner">
              <div className="row row5"><div className="col-12"><VideoOwl cards={baharCards} sliderId="baharSlider" /></div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Mobile (d-none-desktop) dealt-cards overlay — casino-detail ke andar (ab3/ab4
// mobile). Web wale AbVideoCards se farq: shuffle grip nahi, col-9 (col-11 nahi),
// bahar slider mt-1. AB3/AB4 mobile me casino-detail ke top pe render hota hai.
export const AbVideoCardsMobile = memo(function AbVideoCardsMobile({ card = '' }) {
  const { andarCards, baharCards } = splitAbCards(card);
  return (
    <div className="casino-video-cards d-none-desktop">
      <div className="casino-video-cards-container">
        <div className="row row5 align-items-center">
          <div className="col-1">
            <div className="row row5"><div className="col-12 mb-3"><b>A</b></div></div>
            {' '}
            <div className="row row5"><div className="col-12"><b>B</b></div></div>
          </div>
          {' '}
          <div className="col-9">
            <div className="card-inner">
              <div className="row row5"><div className="col-12"><VideoOwl cards={andarCards} sliderId="andarSlider" /></div></div>
            </div>
            {' '}
            <div className="card-inner">
              <div className="row row5 mt-1"><div className="col-12"><VideoOwl cards={baharCards} sliderId="baharSlider" /></div></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// 13-card betting grid for Andar/Bahar. Reference: suspended side (jiske items
// ka b truthy hai) facedown 0.png; active side (b==0) apni rank position image
// (A=1.png..K=13.png). Number = item.l, book hamesha visible (book-black at 0).
const CardsBetBox = memo(function CardsBetBox({ boxClass, titleClass, title, items, isSuspended, onBet, exposures }) {
  return (
    <div className={`${boxClass} text-center${isSuspended ? ' suspended' : ''}`}>
      <h5 className={`w-100 text-center ${titleClass}`}>{title}</h5>
      {' '}
      {items.map((item) => {
        // frontend parity: card face rank ke saath tabhi jab item ka b truthy ho,
        // warna facedown 0.png (reverse map bug fix — pehle isSuspended pe tha)
        const imgNum = isTruthy(item.b) ? getCardPos(item.nat) : 0;
        return (
          <div
            key={item.sid}
            className="casino-card-item"
            onClick={isSuspended ? undefined : () => onBet && onBet(item.sid, 'back', item.l, item)}
          >
            <div className="text-center">{item.l || 0}</div>
            {' '}
            <div className="card-image">
              <img src={`${AB_CARD_BASE}${imgNum}.png`} />
            </div>
            {' '}
            {/* The reference admin never shows a book under the Andar / Bahar
                cards (always a grey 0, bets or not) — the downline's exposure
                there is not displayed. Keep the same slot, no value. */}
            <div className="casino-book book-black">0</div>
          </div>
        );
      })}
    </div>
  );
});

// Andar Bahar 150 (ab4) betting grid — casino-detail me sirf ab-bg (13-card
// Andar/Bahar boxes). Dealt-card slider AB4 page ke casino-video me hai
// (AbVideoCards). Selections data.child me (13 Andar sid 1-13, 13 Bahar 21-33).
const BetTableAB4 = memo(function BetTableAB4({ gameData, onBet, exposures }) {
  const child = gameData?.child ?? [];
  const sub = gameData?.sub ?? [];

  const andarItems = child.filter((c) => String(c.nat).startsWith('Andar'));
  const baharItems = child.filter((c) => String(c.nat).startsWith('Bahar'));

  // frontend parity: side suspended jab uske SAB items ka b falsy/0 ho (ya
  // global gstatus SUSPENDED). Pehle isTruthy pe tha — reverse map bug.
  const globalSusp = sub.length > 0 && (sub[0].gstatus === 'SUSPENDED' || sub[0].gstatus === '0');
  const andarSusp = globalSusp || (andarItems.length > 0 && andarItems.every((c) => !isTruthy(c.b)));
  const baharSusp = globalSusp || (baharItems.length > 0 && baharItems.every((c) => !isTruthy(c.b)));

  return (
    <div className="ab-bg">
      <CardsBetBox
        boxClass="andar-cards-box"
        titleClass="text-playera"
        title="Andar"
        items={andarItems}
        isSuspended={andarSusp}
        onBet={onBet}
        exposures={exposures}
      />
      {' '}
      <CardsBetBox
        boxClass="bahar-cards-box"
        titleClass="text-playerb"
        title="Bahar"
        items={baharItems}
        isSuspended={baharSusp}
        onBet={onBet}
        exposures={exposures}
      />
    </div>
  );
});

export default BetTableAB4;
